import React, { useContext, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import moment from 'moment'
import API from '../../../../services/API'
import InvoiceGenerator from '../../../events/SingleEvent/Invoicing/InvoiceGenerator'
import DetailsBox from '../../../../components/Cards/DetailsBox'
import NumberCardTwo from '../../../../components/Cards/NumberCardTwo'
import Loading from '../../../loading/loadingPage'
import RecordPayment from './RecordPayment'
//@MUI
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Stack from '@mui/material/Stack'
import Grid from '@mui/material/Grid'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import PDFInvoice from './PDFInvoice'
import CreditNoteDialog from './CreditNoteDialog'
import EditDraftInvoiceDialog from './EditDraftInvoiceDialog'
import { GlobalContext, TenantSettingsContext } from '../../../../App'
import { vatMultiplierForItem } from '../../../../helpers/region'
import { useDocumentTitle } from '../../../../helpers/DocumentTitleManager'

// Round VAT per line, matching the server's totals
const lineItemsTotal = (items, region) =>
  (items || []).reduce(
    (total, item) =>
      total +
      (Number(item.quantity) || 0) *
        (Number(item.unitPrice) || 0) *
        vatMultiplierForItem(item.vat, region),
    0,
  )

const STATUS_COLORS = {
  Draft: 'default',
  Approved: 'info',
  Sent: 'primary',
  'Partially Paid': 'warning',
  Paid: 'success',
  Overdue: 'error',
}

const SingleInvoice = () => {
  const { orgConfig, tenantInfo } = useContext(TenantSettingsContext)
  const { orgSettings } = { ...orgConfig }
  const region = tenantInfo?.region
  const { invoiceID } = useParams()
  const { setPageTitle } = useDocumentTitle()
  const { setToast } = useContext(GlobalContext)
  const [invoice, setInvoice] = useState({})
  const [fetchError, setFetchError] = useState(false)
  const [loading, setLoading] = useState(true)
  const [actionBusy, setActionBusy] = useState(false)
  const [openRecordPayment, setOpenRecordPayment] = useState(false)
  const [openEdit, setOpenEdit] = useState(false)
  const [openPDF, setOpenPDF] = useState(false)
  const [openCreditNote, setOpenCreditNote] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [confirmReissue, setConfirmReissue] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    if (invoice?.reference) {
      setPageTitle(`Invoice ${invoice?.reference}`)
    }
  }, [invoice, setPageTitle])

  // All figures VAT-inclusive, matching the server's netAmount maths - the
  // old ex-VAT cards under-reported every total by the VAT amount
  const totalInvoiceAmount = lineItemsTotal(invoice?.items, region)
  const totalPaidAmount =
    invoice?.payments?.reduce(
      (total, payment) => total + (Number(payment.amount) || 0),
      0,
    ) || 0
  const totalCreditedAmount =
    invoice?.creditNotes?.reduce(
      (total, note) => total + lineItemsTotal(note.items, region),
      0,
    ) || 0
  const totalRemainingAmount =
    Math.round(
      (totalInvoiceAmount -
        Number(invoice?.discount || 0) -
        totalCreditedAmount -
        totalPaidAmount) *
        100,
    ) / 100

  const loadInvoice = () => {
    setLoading(true)
    setFetchError(false)
    API.get('/finance/invoice/' + invoiceID)
      .then((res) => {
        if (res.status === 200 && res.data?._id) {
          setInvoice(res.data)
        } else {
          setFetchError(res?.data?.error || 'Unable to load this invoice.')
        }
      })
      .catch(() => setFetchError('Unable to load this invoice.'))
      .finally(() => setLoading(false))
  }

  // Re-fetch when navigating between invoices (e.g. straight to a reissue)
  useEffect(() => loadInvoice(), [invoiceID])

  const runAction = async (request, successMessage) => {
    setActionBusy(true)
    try {
      const res = await request()
      if (res.status === 200) {
        setInvoice((prev) => ({ ...prev, ...res.data }))
        setToast({ message: successMessage })
      } else {
        setToast({
          message: res?.data?.error || 'Unable to update this invoice.',
          severity: 'error',
        })
      }
    } catch {
      setToast({
        message: 'Unable to update this invoice. Please try again later.',
        severity: 'error',
      })
    } finally {
      setActionBusy(false)
    }
  }

  const approveInvoice = () =>
    runAction(
      () =>
        API.put(`/finance/invoice/${invoiceID}/status`, { status: 'Approved' }),
      'Invoice approved',
    )
  const sendInvoice = () =>
    runAction(
      () => API.post(`/finance/invoice/${invoiceID}/send`),
      'Invoice sent',
    )
  const markPaid = () =>
    runAction(
      () => API.post(`/finance/invoice/${invoiceID}/mark-paid`),
      'Invoice marked as paid',
    )

  const deleteDraft = async () => {
    setConfirmDelete(false)
    setActionBusy(true)
    try {
      const res = await API.delete(`/finance/invoice/${invoiceID}`)
      if (res.status === 200) {
        setToast({ message: 'Draft invoice deleted' })
        navigate('/invoices')
      } else {
        setToast({
          message: res?.data?.error || 'Unable to delete this invoice.',
          severity: 'error',
        })
      }
    } catch {
      setToast({
        message: 'Unable to delete this invoice. Please try again later.',
        severity: 'error',
      })
    } finally {
      setActionBusy(false)
    }
  }

  const reissueInvoice = async () => {
    setConfirmReissue(false)
    setActionBusy(true)
    try {
      // creditInFull: any remaining billed value is credited as part of the
      // reissue, so the replacement never double-bills the same work
      const res = await API.post(`/finance/invoice/${invoiceID}/reissue`, {
        creditInFull: true,
      })
      if (res.status === 201) {
        setToast({ message: `Reissued as ${res.data?.reference}` })
        navigate(`/invoices/${res.data._id}`)
      } else {
        setToast({
          message: res?.data?.error || 'Unable to reissue this invoice.',
          severity: 'error',
        })
      }
    } catch {
      setToast({
        message: 'Unable to reissue this invoice. Please try again later.',
        severity: 'error',
      })
    } finally {
      setActionBusy(false)
    }
  }

  if (!loading && fetchError) {
    return (
      <Stack gap={2} alignItems="flex-start">
        <Typography variant="title2">Invoice</Typography>
        <Alert severity="error">{fetchError}</Alert>
      </Stack>
    )
  }

  const status = invoice?.status || 'Draft'

  return loading ? (
    <Loading />
  ) : (
    <Stack gap={1}>
      <Stack direction="row" alignItems="center" gap={1.5}>
        <Typography variant="title2">
          Invoice {invoice?.reference ?? ''}
        </Typography>
        <Chip
          size="small"
          label={status}
          color={STATUS_COLORS[status] || 'default'}
        />
      </Stack>
      <Grid container spacing={2}>
        <NumberCardTwo
          label="Total (inc. VAT)"
          number={`${orgSettings?.currency ?? '£'}${totalInvoiceAmount.toFixed(2)}`}
          lg={3}
          color={8}
          loading={loading}
        />
        <NumberCardTwo
          label="Paid"
          number={`${orgSettings?.currency ?? '£'}${totalPaidAmount.toFixed(2)}`}
          lg={3}
          loading={loading}
          color={totalPaidAmount < totalInvoiceAmount ? 1 : 9}
        />
        <NumberCardTwo
          label="Credited"
          number={`${orgSettings?.currency ?? '£'}${totalCreditedAmount.toFixed(2)}`}
          lg={3}
          loading={loading}
          color={totalCreditedAmount > 0 ? 1 : 9}
        />
        <NumberCardTwo
          label="Outstanding"
          loading={loading}
          number={`${orgSettings?.currency ?? '£'}${totalRemainingAmount.toFixed(2)}`}
          lg={3}
          color={totalRemainingAmount >= 1 ? 1 : 9}
        />
      </Grid>
      <DetailsBox
        title="Invoice Details"
        withBorder
        sx={{ marginTop: 2 }}
        rows={[
          {
            label: 'Date',
            value: moment(invoice.invoiceDate).format('Do MMM YYYY'),
          },
          {
            label: 'Due',
            value: moment(invoice.dueDate).format('Do MMM YYYY'),
          },
          {
            label: 'Client',
            value: invoice.organisation?._id ? (
              <Link to={`/clients/${invoice.organisation._id}`} target="_blank">
                {invoice?.organisation?.orgName ?? ''}
              </Link>
            ) : (
              invoice?.patientCustomer?.name || ''
            ),
          },
          {
            label: invoice.source === 'PTS' ? 'PTS Booking' : 'Event',
            value:
              invoice.source === 'PTS' && invoice.ptsBooking?._id ? (
                <Link
                  to={`/pts/bookings/${invoice.ptsBooking._id}`}
                  target="_blank"
                >
                  {invoice.ptsBooking.bookingRef ?? ''}
                </Link>
              ) : invoice.event?._id ? (
                <Link to={`/events/${invoice.event._id}`} target="_blank">
                  {invoice.event.title ?? ''}
                </Link>
              ) : (
                ''
              ),
          },
        ]}
      />
      <Stack direction="row" flexWrap="wrap" gap={1.5}>
        {status === 'Draft' && (
          <Button
            variant="contained"
            color="anGreen"
            size="small"
            disabled={actionBusy}
            onClick={approveInvoice}
          >
            Approve
          </Button>
        )}
        {status !== 'Draft' && (
          <Button
            variant="contained"
            color="anMainBlue"
            size="small"
            disabled={actionBusy}
            onClick={sendInvoice}
          >
            {invoice.sentAt ? 'Resend' : 'Send'}
          </Button>
        )}
        {status !== 'Draft' && status !== 'Paid' && (
          <>
            <Button
              variant="contained"
              color="anMainBlue"
              size="small"
              onClick={() => setOpenRecordPayment(true)}
            >
              Record Payment
            </Button>
            <Button
              variant="outlined"
              color="anGreen"
              size="small"
              disabled={actionBusy}
              onClick={markPaid}
            >
              Mark Paid
            </Button>
          </>
        )}
        {/* Drafts are fully editable and deletable; once approved the
            invoice is a fixed document - corrections go through a credit
            note, and re-billing goes through Reissue */}
        {status === 'Draft' && (
          <>
            <Button
              variant="contained"
              color="anMainBlue"
              size="small"
              onClick={() => setOpenEdit(true)}
            >
              Edit Invoice
            </Button>
            <Button
              variant="outlined"
              color="anRed"
              size="small"
              disabled={actionBusy}
              onClick={() => setConfirmDelete(true)}
            >
              Delete Draft
            </Button>
          </>
        )}
        {status !== 'Draft' && (
          <>
            <Button
              variant="outlined"
              color="anRed"
              size="small"
              onClick={() => setOpenCreditNote(true)}
            >
              Credit Note
            </Button>
            {!invoice.reissuedTo && (
              <Button
                variant="outlined"
                size="small"
                disabled={actionBusy}
                onClick={() => setConfirmReissue(true)}
              >
                Reissue
              </Button>
            )}
          </>
        )}
        <Button
          variant="contained"
          color="anMainBlue"
          size="small"
          onClick={() => setOpenPDF(true)}
        >
          Download PDF
        </Button>
      </Stack>
      <DetailsBox withBorder title="Items" borderColor="primary.main">
        <Table color="primary" variant="soft">
          <TableHead>
            <TableRow>
              <TableCell
                width="52%"
                sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }}
              >
                Description
              </TableCell>
              <TableCell
                width="12%"
                sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }}
              >
                QTY
              </TableCell>
              <TableCell
                width="12%"
                sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }}
              >
                Price
              </TableCell>
              <TableCell
                width="12%"
                sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }}
              >
                SubTotal
              </TableCell>
              <TableCell
                width="12%"
                sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }}
              >
                VAT
              </TableCell>
              <TableCell width="12%" sx={{ fontWeight: 'bold' }}>
                Total
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(invoice.items || []).map((item) => (
              <TableRow key={item.id}>
                <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                  {item.description}
                </TableCell>
                <TableCell
                  sx={{ borderRight: '1px solid #e0e0e0' }}
                  align="center"
                >
                  {item.quantity}
                </TableCell>
                <TableCell
                  sx={{
                    borderRight: '1px solid #e0e0e0',
                  }}
                >
                  {orgSettings?.currency ?? '£'}
                  {item.unitPrice}
                </TableCell>
                <TableCell
                  sx={{ borderRight: '1px solid #e0e0e0' }}
                  align="right"
                >
                  {orgSettings?.currency ?? '£'}
                  {item.quantity * item.unitPrice}
                </TableCell>
                <TableCell
                  sx={{ borderRight: '1px solid #e0e0e0' }}
                  align="right"
                >
                  {orgSettings?.currency ?? '£'}
                  {(
                    item.quantity *
                    item.unitPrice *
                    (vatMultiplierForItem(item.vat, region) - 1)
                  ).toFixed(2)}
                </TableCell>
                <TableCell align="right">
                  {(item.quantity ?? 0) * (item.unitPrice ?? 0) > 0 &&
                    `${orgSettings?.currency ?? '£'}${parseFloat(
                      (item.quantity ?? 0) *
                        (item.unitPrice ?? 0) *
                        vatMultiplierForItem(item.vat, region),
                    ).toFixed(2)}`}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DetailsBox>
      <DetailsBox withBorder title="Payments" borderColor="primary.main">
        <Table color="primary" variant="soft">
          <TableHead>
            <TableRow>
              <TableCell
                width="20%"
                sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }}
              >
                Date
              </TableCell>
              <TableCell
                width="20%"
                sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }}
              >
                Amount
              </TableCell>
              <TableCell
                width="15%"
                sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }}
              >
                Method
              </TableCell>
              <TableCell
                width="45%"
                sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }}
              >
                Note
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(invoice.payments || []).map((payment, index) => (
              <TableRow key={index}>
                <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                  {moment(payment.date).format('DD/MM/YYYY')}
                </TableCell>
                <TableCell
                  sx={{ borderRight: '1px solid #e0e0e0' }}
                  align="right"
                >
                  {orgSettings?.currency ?? '£'}
                  {parseFloat(payment.amount).toFixed(2)}
                </TableCell>
                <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                  {payment.method || '—'}
                </TableCell>
                <TableCell
                  sx={{
                    borderRight: '1px solid #e0e0e0',
                  }}
                >
                  {payment.note}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DetailsBox>
      {invoice.creditNotes?.length > 0 && (
        <DetailsBox withBorder title="Credit Notes" borderColor="primary.main">
          <Table color="primary" variant="soft">
            <TableHead>
              <TableRow>
                <TableCell
                  width="15%"
                  sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }}
                >
                  Reference
                </TableCell>
                <TableCell
                  width="15%"
                  sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }}
                >
                  Date
                </TableCell>
                <TableCell
                  width="15%"
                  sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }}
                >
                  Amount
                </TableCell>
                <TableCell
                  width="55%"
                  sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }}
                >
                  Reason
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoice.creditNotes.map((note, index) => (
                <TableRow key={index}>
                  <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                    {note.reference}
                  </TableCell>
                  <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                    {moment(note.date).format('DD/MM/YYYY')}
                  </TableCell>
                  <TableCell
                    sx={{ borderRight: '1px solid #e0e0e0' }}
                    align="right"
                  >
                    {orgSettings?.currency ?? '£'}
                    {(note.items || [])
                      .reduce(
                        (sum, item) =>
                          sum +
                          (Number(item.quantity) || 0) *
                            (Number(item.unitPrice) || 0),
                        0,
                      )
                      .toFixed(2)}
                  </TableCell>
                  <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                    {note.reason}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DetailsBox>
      )}
      <RecordPayment
        open={openRecordPayment}
        onClose={() => setOpenRecordPayment(false)}
        invoice={invoice}
        setInvoice={setInvoice}
        amountOutstanding={totalRemainingAmount}
      />
      {invoice.source !== 'PTS' ? (
        <InvoiceGenerator
          open={openEdit}
          onClose={() => setOpenEdit(false)}
          existingInvoice={invoice}
          loadInvoices={loadInvoice}
        />
      ) : (
        <EditDraftInvoiceDialog
          invoice={openEdit ? invoice : null}
          currency={orgSettings?.currency ?? '£'}
          region={region}
          onClose={() => setOpenEdit(false)}
          onSaved={loadInvoice}
        />
      )}
      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <DialogTitle>Delete draft invoice {invoice?.reference}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            The draft and its reference number are removed permanently. The
            number becomes a gap in the sequence.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={deleteDraft}>
            Delete Draft
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={confirmReissue} onClose={() => setConfirmReissue(false)}>
        <DialogTitle>Reissue invoice {invoice?.reference}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Any remaining billed value is credited in full, and a new draft
            invoice is created with the same line items for you to amend and
            approve. The original invoice stays on record, linked to its
            replacement.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmReissue(false)}>Cancel</Button>
          <Button variant="contained" onClick={reissueInvoice}>
            Credit in Full & Reissue
          </Button>
        </DialogActions>
      </Dialog>
      {/* The finance credit-note route takes either source */}
      <CreditNoteDialog
        invoice={openCreditNote ? invoice : null}
        currency={orgSettings?.currency ?? '£'}
        region={region}
        onClose={() => setOpenCreditNote(false)}
        onIssued={loadInvoice}
      />
      <PDFInvoice
        open={openPDF}
        onClose={() => setOpenPDF(false)}
        invoice={invoice}
      />
    </Stack>
  )
}

export default SingleInvoice
